import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion, useInView, useReducedMotion, type Variants } from 'framer-motion';
import { ArrowRight, Play, X } from 'lucide-react';
import Lenis from 'lenis';
import AnimatedOwlIcon from '../components/AnimatedOwlIcon';
import { useIsMobile } from '../hooks/useIsMobile';
import GradientWaves from '../components/reactbits/GradientWaves';
import { HoverBorderGradient } from '../components/ui/hover-border-gradient';
import HowItWorksRevamp from '../components/landing/HowItWorksRevamp';
import TrustStripRevamp from '../components/landing/TrustStripRevamp';
import FeaturesRevamp from '../components/landing/FeaturesRevamp';
import ProblemRevamp from '../components/landing/ProblemRevamp';
// Below the fold, and it pulls in the animated-icon library — load on demand.
const FoundersRevamp = lazy(() => import('../components/landing/FoundersRevamp'));
import PricingRevamp from '../components/landing/PricingRevamp';
import FAQRevamp from '../components/landing/FAQRevamp';
import GuidesRevamp from '../components/landing/GuidesRevamp';
import FinalCtaRevamp from '../components/landing/FinalCtaRevamp';
import FooterRevamp from '../components/landing/FooterRevamp';
import WaitlistModalTrigger from '../components/landing/WaitlistModalTrigger';
// Only used by the commented-out h1 below (kept for reference alongside it).
// import FlipWords from '../components/landing/FlipWords';

// const HERO_AUDIENCE_WORDS = ['freelancers', 'creators', 'agencies'];

// How long the full-bleed mark holds on screen before docking into the navbar.
const HOLD_MS = 850;
// Duration of the dock transition itself — the shared layoutId FLIP below.
const DOCK_SECONDS = 0.9;

const staggerParent: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};

const staggerItem: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};


/**
 * Landing page revamp — hero section only.
 *
 * The owl mark ("Hoot") is Defellix's one truly distinctive asset: its irises
 * already track the cursor (see AnimatedOwlLogo). A mascot that never stops
 * watching is exactly the pitch of a tamper-evident evidence ledger, so the
 * opening moment spends real animation budget on it — full-bleed, then a
 * single continuous shared-layout transition into the navbar slot — rather
 * than a generic corner logo from frame one. Everything else on the page
 * waits for that transition to land before it stages in.
 *
 * Now the real homepage ("/"), also still reachable at /landing-revamp.
 * The previous homepage moved to /legacy-landing, gated behind the admin
 * key (AdminKeyGate) rather than deleted.
 */
export default function LandingRevamp() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  // Reduced motion: skip straight to the docked, fully-staged state.
  const [docked, setDocked] = useState(() => prefersReducedMotion === true);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const t = setTimeout(() => setDocked(true), HOLD_MS);
    return () => clearTimeout(t);
  }, [prefersReducedMotion]);

  // Hero product-demo video. The card in the hero is just a blurred still
  // of /video-banner.webp with "Product walkthrough" on top — clicking it
  // opens the real video (public/Defellix_1_1.mp4) in a lightbox over a
  // dimmed backdrop, autoplaying.
  const videoRef = useRef<HTMLVideoElement>(null);
  const seekBarRef = useRef<HTMLDivElement>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0); // 0–1
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const toggleVideoPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const seekVideo = (clientX: number) => {
    const bar = seekBarRef.current;
    const video = videoRef.current;
    if (!bar || !video || !video.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    video.currentTime = ratio * video.duration;
  };

  const formatVideoTime = (seconds: number) => {
    if (!Number.isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!videoModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVideoModalOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      videoRef.current?.pause();
      // Reset so the next open starts clean rather than mid-scrub / paused.
      setVideoPlaying(true);
      setVideoProgress(0);
      setVideoTime(0);
    };
  }, [videoModalOpen]);

  // The navbar hides once the final CTA + footer stretch scrolls into view
  // (any part of it counts) and reappears the moment the user scrolls back
  // above it.
  const ctaAndFooterRef = useRef<HTMLDivElement>(null);
  const ctaOrFooterInView = useInView(ctaAndFooterRef, { amount: 0 });

  // Lenis smooth scroll — same library/config family as the live
  // LandingPage.tsx, minus the GSAP ScrollTrigger sync (nothing on this
  // page uses GSAP anymore; framer-motion's useScroll already reads real
  // native scroll position, which is exactly what Lenis smooths, not a
  // virtualized one). Skipped entirely under prefers-reduced-motion.
  const lenisRef = useRef<Lenis | null>(null);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });
    lenisRef.current = lenis;

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    document.documentElement.classList.add('lenis');

    return () => {
      lenisRef.current = null;
      cancelAnimationFrame(rafId);
      lenis.destroy();
      document.documentElement.classList.remove('lenis');
    };
  }, [prefersReducedMotion]);

  // Nav links route through Lenis (when it's running) so the jump to a
  // section is the same smooth, interruptible scroll as everywhere else on
  // the page, rather than the browser's own separate scrollIntoView easing.
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(el);
    } else {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <LayoutGroup>
      {/* min-h-dvh, not min-h-screen (100vh) — the hero frame below is
          already min-h-[98dvh]; mixing a static vh outer wrapper with a
          dynamic dvh inner frame is what was causing the hero to visibly
          jump/glitch on mobile as the browser chrome collapses on scroll
          (the two boxes were resizing independently at different times). */}
      <div className="min-h-dvh bg-black text-[#F5F6F2]">
        {/* ── Navbar — the mark's landing pad, centered ─────────────── */}
        <motion.nav
          animate={{ y: ctaOrFooterInView ? -96 : 0, opacity: ctaOrFooterInView ? 0 : 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed top-0 inset-x-0 h-20 z-[100] px-6 md:px-12 flex items-center justify-between border-b border-white/[0.06] bg-black/40 backdrop-blur-md ${ctaOrFooterInView ? 'pointer-events-none' : ''
            }`}
        >
          {/* Left zone — the mark on mobile (single instance shared with the
              center zone below via useIsMobile, since layoutId="hoot-mark"
              can only safely be mounted in one place at a time), in-page
              section links + real Guides page link on desktop. */}
          <div className="flex items-center h-full">
            {docked && isMobile && (
              <motion.div layoutId="hoot-mark" transition={{ duration: DOCK_SECONDS, ease: [0.22, 1, 0.36, 1] }}>
                <AnimatedOwlIcon className="w-9 h-9" />
              </motion.div>
            )}
            <AnimatePresence>
              {docked && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="hidden md:flex items-center gap-7"
                >
                  <Link to="/signup" className="text-sm text-white/60 hover:text-white transition-colors">
                    Create a contract
                  </Link>
                  <a
                    href="#features"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('features');
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Product
                  </a>
                  <a
                    href="#pricing"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('pricing');
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                  <a
                    href="#faq"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('faq');
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    FAQ
                  </a>
                  <a
                    href="#guides"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('guides');
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Guides
                  </a>
                  {/* Reuses the same waitlist-modal trigger as the hero's
                      "1 month free" offer (WaitlistModalTrigger + the real
                      early-access form) — a separate, unique layoutId since
                      the hero's own trigger uses "hero-waitlist-cta". */}
                  <WaitlistModalTrigger layoutId="navbar-waitlist-cta" triggerRadius="8px">
                    <button
                      type="button"
                      className="text-sm font-semibold text-accent hover:text-[#5cd06f] transition-colors cursor-pointer"
                    >
                      Free Access
                    </button>
                  </WaitlistModalTrigger>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Center — the mark's landing pad, desktop only (mobile's copy
              lives in the left zone above). */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center">
            {docked && !isMobile && (
              <motion.div layoutId="hoot-mark" transition={{ duration: DOCK_SECONDS, ease: [0.22, 1, 0.36, 1] }}>
                <AnimatedOwlIcon className="w-10 h-10" />
              </motion.div>
            )}
          </div>

          <AnimatePresence>
            {docked && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="flex items-center gap-2 md:gap-4"
              >
                <Link
                  to="/login"
                  className="relative text-sm font-medium text-white/70 hover:text-white transition-colors cursor-pointer after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-accent after:transition-all after:duration-300 hover:after:w-full"
                >
                  Log in
                </Link>
                <HoverBorderGradient
                  as="a"
                  containerClassName="group rounded-full"
                  className="flex items-center gap-1.5 bg-accent text-black text-sm font-semibold px-4 py-1.5 md:px-5 md:py-2"
                  {...({ href: '/signup' } as object)}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/signup');
                  }}
                >
                  Start free
                  <ArrowRight size={14} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </HoverBorderGradient>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>

        {/* ── Full-bleed intro stage ─────────────────────────────────── */}
        <AnimatePresence>
          {!docked && (
            <motion.div
              key="intro-stage"
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
            >
              <motion.div
                layoutId="hoot-mark"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <AnimatedOwlIcon className="w-[34vw] max-w-[240px] min-w-[120px] h-auto" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Hero frame — one box: background, waves, and all content ─── */}
        {/* min-h (not h) so the box is at least a full screen but grows with
            the content instead of clipping or scrolling internally. The
            waves live in an absolutely-positioned layer, so they stretch to
            whatever height the content ends up giving the box. */}
        <div className="relative mx-auto w-[98vw] md:min-h-[98dvh] overflow-hidden rounded-b-3xl bg-black">
          {/* GradientWaves (React Bits) — brand-tinted, at the library's own
              default motion values; only the palette is ours. */}
          <div className="absolute inset-0">
            <GradientWaves
              horizonColor="#04140a"
              waveColor="#1f6e34"
              crestColor="#9df5b4"
              speed={0.4}
              amplitude={2.5}
              waveScale={0.6}
              waveRatio={0.9}
              swell={35}
              turbulence={20}
              tilt={1.11}
              zoom={1.0}
              height={5.5}
              fogDepth={15}
              detail="medium"
              brightness={1.0}
              opacity={1.0}
              mouseInteraction
              parallaxStrength={0.5}
              grain
              grainIntensity={0.05}
            />
          </div>

          {/* Legibility scrim — light touch, and only near the very top/bottom
              edges (nav clearance, frame seam). It used to run the full
              gradient across `inset-0`, which stretches with the box: once
              content made the box tall, the "to-black/85" stop landed right
              behind the headline/CTA/video and crushed the waves to near-
              black through the whole content area. Contrast for the copy
              itself now comes from text-shadow on the text elements instead
              of a global wash, so the background stays visible throughout. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/25" />

          {/* ── Hero content — stages in once the mark has landed ────────── */}
          {/* min-h-[98dvh] + justify-center on desktop centers the content
              within a full-screen frame. On mobile that forced height left
              a large dead gap below the video card (content is naturally
              shorter than 98dvh there), so mobile drops the forced height
              and just hugs its own content instead, with a smaller trailing
              pb. */}
          <section className="relative z-10 md:min-h-[98dvh] pt-32 pb-10 md:pb-24 px-6 flex flex-col items-center justify-center text-center">
            <motion.div
              variants={staggerParent}
              initial="hidden"
              animate={docked ? 'show' : 'hidden'}
              className="w-full flex flex-col items-center"
            >
              {/* Headline gets its own wide box (not the max-w-3xl used
                  below) — a single 48-character line at hero size genuinely
                  needs more than 768px of room. flex-nowrap only kicks in at
                  md+: forcing one line on a phone at a size that still reads
                  as a hero headline isn't physically possible, so mobile
                  keeps flex-wrap and a smaller clamp ceiling instead of
                  shrinking to illegible text. */}
              {/* <motion.h1
                variants={staggerItem}
                className="w-full max-w-6xl mx-auto font-syne font-extrabold text-[clamp(1.75rem,1rem+2.4vw,2.75rem)] leading-[1.15] tracking-tight text-[#F5F6F2] [text-shadow:0_6px_28px_rgba(0,0,0,0.55)] flex flex-wrap md:flex-nowrap items-baseline justify-center gap-x-3 whitespace-nowrap"
              >
                <span>Legal protection for</span>
                <FlipWords
                  words={HERO_AUDIENCE_WORDS}
                  duration={2200}
                  className="text-transparent bg-clip-text bg-gradient-to-br from-[#5cd06f] to-[#3cb44f]"
                />
                <span>in 2 minutes.</span>
              </motion.h1> */}

              {/* Two-font system: Plus Jakarta Sans (plain) carries the
                  sentence, Instrument Serif italic (cursive) accents the one
                  word that actually matters here — "protect" is the whole
                  pitch, so it's the word that gets the different voice,
                  not a random pick. */}
              <motion.h1
                variants={staggerItem}
                className="max-w-4xl mx-auto font-bricolage font-extrabold text-[clamp(2.2rem,1.4rem+3vw,4.2rem)] leading-[1.12] tracking-tight text-[#F5F6F2] [text-shadow:0_6px_28px_rgba(0,0,0,0.55)] text-center text-balance"
              >
                One platform to create,{' '}
                <span className="font-instrumentserif italic font-normal text-[1.15em] text-accent">
                  <i>protect</i>
                </span>
                , and manage every project
              </motion.h1>

              <motion.p
                variants={staggerItem}
                className="mt-6 max-w-xl font-bricolage text-base md:text-lg text-white/75 leading-relaxed [text-shadow:0_2px_16px_rgba(0,0,0,0.6)]"
              >
                Court-admissible contracts in 2 minutes, with milestones, approvals, and payments
                tracked end-to-end. <i className='text-accent'>Your first contract is free.</i>
              </motion.p>

              <motion.div variants={staggerItem} className="mt-9 flex items-center justify-center">
                <HoverBorderGradient
                  as="a"
                  containerClassName="group rounded-full shadow-[0_14px_45px_-12px_rgba(60,180,79,0.55)] hover:shadow-[0_18px_55px_-10px_rgba(60,180,79,0.75)] transition-shadow duration-300 active:scale-[0.97]"
                  className="cursor-pointer flex items-center gap-2.5 bg-accent text-black text-base font-bold font-bricolage tracking-wide px-9 py-4"
                  {...({ href: '/signup' } as object)}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/signup');
                  }}
                >
                  Start free
                  <ArrowRight
                    size={18}
                    strokeWidth={2.5}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </HoverBorderGradient>
              </motion.div>

              {/* Video card — a blurred still of /video-banner.webp with only
                  "Product walkthrough" readable on top, not a scrubbable
                  player inline. Clicking it opens the real video in a
                  lightbox instead (below, outside the staggered hero
                  content). scale-110 on the blurred image keeps the blur
                  radius from revealing a sharp, unblurred edge at the card's
                  border. */}
              <motion.div variants={staggerItem} className="mt-16 w-full max-w-7xl">
                <button
                  type="button"
                  onClick={() => setVideoModalOpen(true)}
                  aria-label="Play product walkthrough"
                  className="group relative block w-full aspect-video rounded-3xl border border-white/15 overflow-hidden cursor-pointer"
                >
                  <img
                    src="/video-banner.webp"
                    alt="Preview of the Defellix product walkthrough video"
                    className="absolute inset-0 w-full h-full object-cover blur-xs"
                  />
                  <div className="absolute inset-0 bg-black/45 group-hover:bg-black/35 transition-colors" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                    <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-accent flex items-center justify-center shadow-[0_0_60px_-10px_rgba(60,180,79,0.6)] group-hover:scale-105 transition-transform">
                      <Play size={30} className="text-black ml-1" fill="currentColor" />
                    </span>
                    <span className="text-sm font-semibold tracking-wide text-white uppercase [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
                      Product walkthrough
                    </span>
                  </div>
                </button>
              </motion.div>
            </motion.div>
          </section>
        </div>

        {/* Video lightbox — dimmed backdrop, real video, autoplaying.
            Click outside the video or the close button (or Escape) to
            dismiss; the effect above pauses playback on close. */}
        <AnimatePresence>
          {videoModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
              onClick={() => setVideoModalOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-4xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button lives on this outer, non-clipped wrapper —
                    it was previously a child of the overflow-hidden video
                    frame below, which clipped its negative offset and left
                    it sitting inside the frame instead of straddling
                    outside it. */}
                <button
                  type="button"
                  onClick={() => setVideoModalOpen(false)}
                  aria-label="Close video"
                  className="absolute -top-4 -right-4 w-9 h-9 rounded-full bg-white text-black flex items-center justify-center shadow-lg cursor-pointer hover:scale-105 transition-transform z-10"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>

                <div className="group relative aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl">
                  <video
                    ref={videoRef}
                    poster="/video-banner.webp"
                    autoPlay
                    playsInline
                    className="w-full h-full cursor-pointer"
                    onClick={toggleVideoPlay}
                    onPlay={() => setVideoPlaying(true)}
                    onPause={() => setVideoPlaying(false)}
                    onEnded={() => setVideoModalOpen(false)}
                    onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
                    onTimeUpdate={(e) => {
                      const v = e.currentTarget;
                      setVideoTime(v.currentTime);
                      setVideoProgress(v.duration ? v.currentTime / v.duration : 0);
                    }}
                  >
                    <source src="/Defellix_1_1.mp4" type="video/mp4" />
                  </video>

                  {/* Custom themed controls — brand green fill, mono time,
                      not the browser's default control bar. Fades in on
                      hover once playing so it doesn't sit over the video
                      the whole time; always visible while paused. */}
                  <div
                    className={`absolute inset-x-0 bottom-0 px-4 py-3 flex items-center gap-3 bg-gradient-to-t from-black/85 to-transparent transition-opacity duration-300 ${videoPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
                      }`}
                  >
                    <button
                      type="button"
                      onClick={toggleVideoPlay}
                      aria-label={videoPlaying ? 'Pause' : 'Play'}
                      className="shrink-0 w-8 h-8 rounded-full bg-accent flex items-center justify-center text-black cursor-pointer hover:scale-105 transition-transform"
                    >
                      {videoPlaying ? (
                        <span className="flex gap-[3px]">
                          <span className="w-[3px] h-3 bg-black rounded-full" />
                          <span className="w-[3px] h-3 bg-black rounded-full" />
                        </span>
                      ) : (
                        <Play size={13} className="ml-0.5" fill="currentColor" />
                      )}
                    </button>

                    <div
                      ref={seekBarRef}
                      onClick={(e) => seekVideo(e.clientX)}
                      className="group/seek relative flex-1 h-1 rounded-full bg-white/20 cursor-pointer"
                    >
                      <div
                        className="h-full bg-accent rounded-full"
                        style={{ width: `${videoProgress * 100}%` }}
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-accent opacity-0 group-hover/seek:opacity-100 transition-opacity"
                        style={{ left: `${videoProgress * 100}%` }}
                      />
                    </div>

                    <span className="text-[11px] font-mono text-white/70 tabular-nums shrink-0">
                      {formatVideoTime(videoTime)} / {formatVideoTime(videoDuration)}
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <HowItWorksRevamp />
        <TrustStripRevamp />
        <div id="features" className="scroll-mt-24">
          <FeaturesRevamp />
        </div>
        <ProblemRevamp />
        <Suspense fallback={<div style={{ minHeight: 480 }} />}>
          <FoundersRevamp />
        </Suspense>
        {/* No id wrapper here — PricingRevamp's own <section> already has
            id="pricing"; wrapping it again would duplicate the id. */}
        <PricingRevamp />
        <div id="faq" className="scroll-mt-24">
          <FAQRevamp />
        </div>
        <div id="guides" className="scroll-mt-24">
          <GuidesRevamp />
        </div>
        <div ref={ctaAndFooterRef}>
          <FinalCtaRevamp />
          <FooterRevamp />
        </div>
      </div>
    </LayoutGroup>
  );
}
